import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import svgIcons from "../../../assets/svg/svgIcons";

export interface DropdownOption {
  label: string;
  value: string;
}

interface PortalDropdownProps {
  value?: string;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  min:number;
  max:number;
  onChange: (value: string) => void;
}

const MENU_MAX_HEIGHT = 208;

export default function PortalDropdown({
  value,
  options,
  error,
  placeholder = "Select",
  onChange,
  disabled
}: PortalDropdownProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const values = options.map((o) => Number(o.value));
  const min = Math.min(...values);
  const max = Math.max(...values);

  const updatePosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp =
      spaceBelow < MENU_MAX_HEIGHT &&
      spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      openUp ? spaceAbove - 8 : spaceBelow - 8
    );

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
      maxHeight,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp
        ? window.innerHeight - rect.top + 4
        : undefined,
    });
  };

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(prev => {
      if (!prev) {
        updatePosition();
      }
      return !prev;
    });
  };

  const openDropdown = () => {
    if (!open) {
      updatePosition();
      setOpen(true);
    }
  }

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        !inputRef.current?.contains(e.target as Node) &&
        !dropdownRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  return (
    <>
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onClick={openDropdown}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "") {
              onChange(min.toString());
              return;
            }
            if (!/^\d+$/.test(val)) {
              return;
            }
            let num = Number(val);
            num = Math.max(min, Math.min(max, num));
            onChange(String(num));
            setOpen(true);
          }}
          className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 placeholder:opacity-0 md:placeholder:opacity-100
          ${error ? "border border-red-500 focus:border-red-500" : "border border-gray-300 focus:border-blue-500"}`}
        />

        {!disabled &&
          <div
            className="absolute inset-y-0 right-0 flex items-center px-2 cursor-pointer z-10"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle(e as unknown as React.MouseEvent);
            }}
          >
            <img
              src={svgIcons.ExpandIcon}
              className={`size-5 ${open ? "-rotate-90" : "rotate-90"}`}
            />
          </div>
        }
      </div>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={menuStyle}
            className="overflow-hidden rounded-md border border-gray-300 bg-white shadow-xl"
          >
            <ul
              className="overflow-y-auto"
              style={{ maxHeight: menuStyle.maxHeight }}
            >
              {options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 ${option.value === value
                    ? "bg-blue-100"
                    : ""
                    }`}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
}